import { useEffect, useMemo, useState } from 'react';
import { useSetAtom } from 'jotai';
import { IconButton } from '@carbon/react';
import { ChevronDown, ChevronUp, Location, ZoomIn } from '@carbon/react/icons';
import { LinkOrText } from '../link-or-text';
import { PropertyMiniMaps } from './property-mini-maps';
import { BreakdownCell } from './interest-cells';
import {
  buildAttributeRows,
  wellHref,
  type AttrRow,
  type EconomicsPair,
} from './property-attributes';
import { resolveRuleColor, type ColorRule } from './color-by';
import { requestZoomToPropertyAtom } from './atoms';
import type { ComparisonResult } from './types';
import style from './comparison-properties.module.scss';

/**
 * One attribute row of a property card: label + Origin/Target cells. The target
 * cell highlights when the sides differ and shows the green/red delta chip.
 * @param {object} props - the attribute row
 * @return {JSX.Element} A table row
 */
// Renders one side's cell content: the LOE breakdown, or a plain (optionally
// linked) value with the Target-side delta chip.
const CardCell = ({ row, side }: { row: AttrRow; side: 'a' | 'b' }) => {
  if (row.breakdown) return <BreakdownCell row={row} side={side} />;
  const href = side === 'a' ? row.aHref : row.bHref;
  const text = side === 'a' ? row.aText : row.bText;
  return (
    <>
      <LinkOrText href={href} className={style.link}>
        {text}
      </LinkOrText>
      {side === 'b' && row.delta && (
        <span
          className={
            row.delta.positive ? style.deltaPositive : style.deltaNegative
          }
        >
          {' '}
          [{row.delta.text}]
        </span>
      )}
    </>
  );
};

const CardRow = ({ row }: { row: AttrRow }) => (
  <tr>
    <th scope="row" className={style.rowHeader}>
      {row.label}
    </th>
    <td className={style.value}>
      <CardCell row={row} side="a" />
    </td>
    <td className={`${style.value} ${row.differs ? style.diff : ''}`}>
      <CardCell row={row} side="b" />
    </td>
  </tr>
);

interface PropertyCardProps {
  result: ComparisonResult;
  /** When true, all cards show their maps (the toolbar "Show all maps" toggle). */
  showAllMaps: boolean;
  /** Section-wide attribute expansion: true = all attributes, false = only differences. */
  expandAll: boolean;
  /** Bumped each time a section-wide expand button is pressed, to re-sync cards. */
  expandNonce: number;
  /** Enabled color rules that apply to property tables (drives the color dot). */
  tableRules: ColorRule[];
  /** Both sides' scenario economics, for the per-unit economic rows. */
  economics: EconomicsPair | null;
}

/**
 * One property comparison card (mock v16): a `{uwi} • {well name}` header, a
 * map-pin that swaps the attribute table for Origin/Target maps, and a
 * "Show all attributes" / "Show only differences" toggle. By default only the
 * differing attributes show; expanding reveals every attribute.
 * @param {PropertyCardProps} props - the property result and show-all-maps flag
 * @return {JSX.Element} The property card
 */
export const PropertyCard = ({
  result,
  showAllMaps,
  expandAll,
  expandNonce,
  tableRules,
  economics,
}: PropertyCardProps) => {
  // null = follow the section-wide expand buttons; true/false = per-card override.
  const [attrsOverride, setAttrsOverride] = useState<boolean | null>(null);
  // A section-wide "Show all attributes"/"Show all differences" press re-syncs
  // every card back to the global choice, discarding per-card overrides.
  useEffect(() => setAttrsOverride(null), [expandNonce]);
  const showAllAttrs = attrsOverride ?? expandAll;
  // null = follow the global "Show all maps"; true/false = per-card override.
  const [mapOverride, setMapOverride] = useState<boolean | null>(null);
  const showMap = mapOverride ?? showAllMaps;

  const { uwi, wellName, uniqueSlotId } = result;
  const requestZoom = useSetAtom(requestZoomToPropertyAtom);
  const rows = useMemo(
    () => buildAttributeRows(result, economics),
    [result, economics]
  );
  const visibleRows = showAllAttrs ? rows : rows.filter((r) => r.differs);
  const dotColor = resolveRuleColor(result, tableRules);

  return (
    <div className={style.card}>
      <div className={style.cardHeader}>
        <div className={style.cardTitle}>
          {dotColor && (
            <span
              className={style.colorDot}
              style={{ backgroundColor: dotColor }}
              aria-hidden="true"
            />
          )}
          {uwi ? (
            <>
              <LinkOrText href={wellHref(uwi)} className={style.link}>
                {uwi}
              </LinkOrText>
              {wellName && <span className={style.wellName}>• {wellName}</span>}
            </>
          ) : (
            <span className={style.wellName}>{uniqueSlotId}</span>
          )}
        </div>
        <div className={style.cardActions}>
          <IconButton
            kind="ghost"
            size="sm"
            label="Zoom to on map"
            align="bottom"
            onClick={() => requestZoom(uniqueSlotId)}
          >
            <ZoomIn />
          </IconButton>
          <IconButton
            kind="ghost"
            size="sm"
            label={showMap ? 'Hide maps' : 'Show maps'}
            align="bottom"
            isSelected={showMap}
            onClick={() => setMapOverride(!showMap)}
          >
            <Location />
          </IconButton>
          <button
            type="button"
            className={style.toggle}
            onClick={() => setAttrsOverride(!showAllAttrs)}
          >
            {showAllAttrs ? 'Show only differences' : 'Show all attributes'}
            {showAllAttrs ? <ChevronUp /> : <ChevronDown />}
          </button>
        </div>
      </div>
      {showMap ? (
        <PropertyMiniMaps result={result} />
      ) : (
        <table className={style.table}>
          <thead>
            <tr>
              <th scope="col" className={style.rowHeader}>
                Package Evals
              </th>
              <th scope="col" className={style.value}>
                Origin
              </th>
              <th scope="col" className={style.value}>
                Target
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => <CardRow key={row.key} row={row} />)
            ) : (
              <tr>
                <td className={style.value} colSpan={3}>
                  No differing attributes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};
